
//////////////////////////////////////////////////
//  Web interface functions
// Uses LocalStorage for project storage and HTML5 Audio for sound
//////////////////////////////////////////////////

let mediaStore = {}; // Stores media data
let projectStore = []; // Stores projects
let currentId = 1;

const DB_KEY = 'scratchjr_projects';
const MEDIA_KEY = 'scratchjr_media';

// Load from localStorage on init
try {
    const savedProjects = localStorage.getItem(DB_KEY);
    if (savedProjects) {
        projectStore = JSON.parse(savedProjects);
        if (projectStore.length > 0) {
            currentId = Math.max(...projectStore.map(p => p.id)) + 1;
        }
    }
    
    const savedMedia = localStorage.getItem(MEDIA_KEY);
    if (savedMedia) {
        mediaStore = JSON.parse(savedMedia);
    }
} catch (e) {
    console.log('Could not load from localStorage:', e);
}

function saveToStorage() {
    try {
        localStorage.setItem(DB_KEY, JSON.stringify(projectStore));
        localStorage.setItem(MEDIA_KEY, JSON.stringify(mediaStore));
    } catch (e) {
        console.log('Could not save to localStorage:', e);
    }
}

function executeSQL(sql, values, isQuery) {
    // Simple SQL parser for our needs
    if (sql.toLowerCase().startsWith('insert')) {
        const newProject = { id: currentId++, deleted: 'NO', mtime: (new Date()).getTime().toString(), isgift: '0' };
        const parts = sql.match(/insert into (\w+) \(([^)]+)\) values \(([^)]+)\)/i);
        if (parts) {
            const keys = parts[2].split(',').map(k => k.trim());
            const vals = values;
            keys.forEach((key, i) => {
                newProject[key] = vals[i];
            });
        }
        projectStore.push(newProject);
        saveToStorage();
        return JSON.stringify([{ id: newProject.id }]);
    } else if (sql.toLowerCase().startsWith('update')) {
        const idMatch = sql.match(/where id = (\d+)/i);
        if (idMatch) {
            const id = parseInt(idMatch[1]);
            const project = projectStore.find(p => p.id === id);
            if (project) {
                const setParts = sql.match(/set (.+?) where/i);
                if (setParts) {
                    const sets = setParts[1].split(',');
                    let valIndex = 0;
                    sets.forEach(set => {
                        const kv = set.split('=').map(s => s.trim());
                        if (kv.length === 2) {
                            project[kv[0]] = values[valIndex++];
                        }
                    });
                }
                project.mtime = (new Date()).getTime().toString();
                saveToStorage();
                return JSON.stringify([{ id: id }]);
            }
        }
        return JSON.stringify([]);
    } else if (sql.toLowerCase().startsWith('delete')) {
        const idMatch = sql.match(/where id = (\d+)/i);
        if (idMatch) {
            const id = parseInt(idMatch[1]);
            projectStore = projectStore.filter(p => p.id !== id);
            saveToStorage();
            return JSON.stringify([{ id: id }]);
        }
        return JSON.stringify([]);
    } else if (sql.toLowerCase().startsWith('select')) {
        const whereMatch = sql.match(/where (.+?)( order by|$)/i);
        let filteredProjects = [...projectStore];
        
        if (whereMatch) {
            const whereClause = whereMatch[1];
            const condParts = whereClause.split('=');
            if (condParts.length === 2) {
                const key = condParts[0].trim();
                const val = values[0];
                filteredProjects = projectStore.filter(p => p[key] === val);
            }
        }
        
        const orderMatch = sql.match(/order by (\w+)/i);
        if (orderMatch) {
            const orderKey = orderMatch[1];
            filteredProjects.sort((a, b) => {
                if (a[orderKey] < b[orderKey]) return 1;
                if (a[orderKey] > b[orderKey]) return -1;
                return 0;
            });
        }
        
        return JSON.stringify(filteredProjects);
    }
    return JSON.stringify([]);
}

let activeSounds = {};

export default class Web {
    // Database functions
    static stmt(json, fcn) {
        const result = executeSQL(json.stmt, json.values, false);
        if (fcn) fcn(result);
    }

    static query(json, fcn) {
        const result = executeSQL(json.stmt, json.values, true);
        if (fcn) fcn(result);
    }

    static setfield(db, id, fieldname, val, fcn) {
        var json = {};
        var keylist = [fieldname + ' = ?', 'mtime = ?'];
        json.values = [val, (new Date()).getTime().toString()];
        json.stmt = 'update ' + db + ' set ' + keylist.toString() + ' where id = ' + id;
        Web.stmt(json, fcn);
    }

    // IO functions
    static cleanassets(ft, fcn) {
        mediaStore = {};
        saveToStorage();
        if (fcn) fcn();
    }

    static getsettings(fcn) {
        if (fcn) fcn(JSON.stringify({}));
    }

    static getmedia(file, fcn) {
        if (mediaStore[file]) {
            fcn(mediaStore[file]);
        } else {
            fcn('');
        }
    }

    static setmedia(str, ext, fcn) {
        const id = 'media_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        mediaStore[id] = str;
        saveToStorage();
        if (fcn) fcn(id);
    }

    static setmedianame(str, name, ext, fcn) {
        mediaStore[name] = str;
        saveToStorage();
        if (fcn) fcn(name);
    }

    static getmd5(str, fcn) {
        // Simple hash (not true MD5, but works for our purposes)
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        if (fcn) fcn(Math.abs(hash).toString(16));
    }

    static remove(str, fcn) {
        delete mediaStore[str];
        saveToStorage();
        if (fcn) fcn();
    }

    static getfile(str, fcn) {
        if (mediaStore[str]) {
            fcn(mediaStore[str]);
        } else {
            fcn('');
        }
    }

    static setfile(name, str, fcn) {
        mediaStore[name] = btoa(str);
        saveToStorage();
        if (fcn) fcn();
    }

    // Sound functions
    static registerSound(dir, name, fcn) {
        if (fcn) fcn();
    }

    static playSound(name, fcn) {
        try {
            if (mediaStore[name]) {
                const audioData = atob(mediaStore[name]);
                const blob = new Blob([new Uint8Array(audioData.length).map((_, i) => audioData.charCodeAt(i))], { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);
                activeSounds[name] = audio;
                audio.onended = () => {
                    URL.revokeObjectURL(url);
                    delete activeSounds[name];
                    if (fcn) fcn();
                };
                audio.play();
            }
        } catch (e) {
            console.log('Error playing sound:', e);
            if (fcn) fcn();
        }
    }

    static stopSound(name, fcn) {
        if (activeSounds[name]) {
            activeSounds[name].pause();
            delete activeSounds[name];
        }
        if (fcn) fcn();
    }

    static sndrecord(fcn) {
        // Web recording is complex, provide dummy implementation
        if (fcn) fcn('');
    }

    static recordstop(fcn) {
        if (fcn) fcn('');
    }

    static volume(fcn) {
        if (fcn) fcn('1');
    }

    static startplay(fcn) {
        if (fcn) fcn();
    }

    static stopplay(fcn) {
        if (fcn) fcn();
    }

    static recorddisappear(b, fcn) {
        if (fcn) fcn();
    }

    static askpermission() {
        // Web permissions handled differently
    }

    static hascamera() {
        return false;
    }

    static startfeed(data, fcn) {
        if (fcn) fcn();
    }

    static stopfeed(fcn) {
        if (fcn) fcn();
    }

    static choosecamera(mode, fcn) {
        if (fcn) fcn();
    }

    static captureimage(fcn) {
        if (fcn) fcn('');
    }

    static hidesplash(fcn) {
        if (fcn) fcn();
    }

    static trace(str) {
        console.log(str);
    }

    static parse(str) {
        console.log(JSON.parse(str));
    }

    static tracemedia(str) {
        console.log(atob(str));
    }

    static ignore() {}

    static createZipForProject(projectData, metadata, name, fcn) {
        // Simple implementation for Web
        if (fcn) fcn(name);
    }

    static sendSjrToShareDialog(fileName, emailSubject, emailBody, shareType, b64data) {
        // Create download link
        const blob = new Blob([Uint8Array.from(atob(b64data), c => c.charCodeAt(0))], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName + '.sjr';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    static deviceName(fcn) {
        if (fcn) fcn('Web Browser');
    }

    static analyticsEvent(category, action, label) {
        console.log('Analytics:', category, action, label);
    }

    static setAnalyticsPlacePref(preferredPlace) {}

    static setAnalyticsPref(key, value) {}
}

window.Web = Web;

